import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPermissions,
  fetchPermissionsById,
} from "slices/permissions/thunk";
import { Accordion, Form } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { Axios } from "api/axios";
import { fetchLatestPermissions } from "slices/auth/login/thunk";
import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { MecordColors } from "Constants/Color";


interface PermissionTabProps {
  id?: string;
}

const PermissionTab: React.FC<PermissionTabProps> = ({ id }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const [openAccordion, setOpenAccordion] = useState<string | undefined>();
  const [selected, setSelected] = useState<any>({});
  const [hasExistingPermissions, setHasExistingPermissions] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false); // For page/data loading
  const [isSubmitting, setIsSubmitting] = useState(false); // For form submission
  const { modules } = useSelector((state: any) => state.Permission);
  const [modulesList, setModulesList] = useState<any[]>([]);


  // Sort modules by sr_no
  useEffect(() => {
    let sortedModules = [...modules];
    sortedModules.sort((a: any, b: any) => a.sr_no - b.sr_no);
    setModulesList(sortedModules);
    // console.log(modulesList, "modulesList, 37")
    // console.log(sortedModules, "sortedModules, 39")
  }, [modules]);


  // Get user_id from prop (id) or from localStorage (after basic details submission)
  const getUserId = () => {
    if (id) return id; // From URL params or prop
    const storedUserId = localStorage.getItem("permission_user");
    if (storedUserId) {
      try {
        const parsed = JSON.parse(storedUserId);
        return typeof parsed === "string" ? parsed : parsed;
      } catch {
        return storedUserId;
      }
    }
    return "";
  };

  const user = getUserId();
  const authUser = JSON.parse(localStorage?.getItem("authUser") || "{}");

  useEffect(() => {
    setIsPageLoading(true);
    dispatch(fetchPermissions())
      .then(() => {
        setIsPageLoading(false);
      })
      .catch(() => {
        setIsPageLoading(false);
      });
  }, [dispatch]);

  // Reset form when creating new user (no user_id available)
  useEffect(() => {
    if (!user) {
      setSelected({});
      setHasExistingPermissions(false);
      setOpenAccordion(undefined);
    }
  }, [user]);

  useEffect(() => {
    console.log("line 55")
    if (user && modulesList.length > 0) {
      setIsPageLoading(true);
      dispatch(fetchPermissionsById(user)).unwrap()
        .then((resp: any) => {
          console.log("resp, line 68", resp)
          // resp is already the apps_permissions array from the thunk
          const userPermissions = Array.isArray(resp) ? resp : [];
          console.log(userPermissions, "userPermissions, line 60")
          if (userPermissions && userPermissions.length > 0) {
            setHasExistingPermissions(true);
          } else {
            setHasExistingPermissions(false);
          }

          const normalized: any = {};
          let firstFullyCheckedApp: string | undefined;
          console.log(user, "user, line 68")
          modulesList.forEach((modApp: any) => {
            const appName = modApp.app_name;
            normalized[appName] = {};

            const matchedApp = userPermissions.find(
              (ap: any) => ap.apps_name === appName
            );
            let appFullyChecked = true;

            Object.entries(modApp.module_name_perm).forEach(
              ([moduleName, actions]: [string, any]) => {
                normalized[appName][moduleName] = {};
                const matchedModule = matchedApp?.modules_permissions?.find(
                  (m: any) => m.modules_name === moduleName
                );

                let moduleFullyChecked = true;

                actions && actions.length > 0 && actions.forEach((action: string) => {
                  const isChecked =
                    matchedModule?.permissions?.[action] || false;
                  normalized[appName][moduleName][action] = isChecked;

                  if (!isChecked) {
                    moduleFullyChecked = false;
                    appFullyChecked = false;
                  }
                });

                // If any module fully checked → open accordion
                if (moduleFullyChecked && !openAccordion) {
                  setOpenAccordion(appName);
                }
              }
            );

            if (appFullyChecked && !firstFullyCheckedApp) {
              firstFullyCheckedApp = appName;
            }
          });

          setSelected(normalized);

          // Open app accordion if entire app fully checked
          if (firstFullyCheckedApp && !openAccordion) {
            setOpenAccordion(firstFullyCheckedApp);
          }
        })
        .catch(() => {
          setHasExistingPermissions(false);
          // toast.error("Failed to fetch user permissions");
        })
        .finally(() => {
          setIsPageLoading(false);
        });
    }
  }, [user, modulesList, dispatch]);

  const toggleAccordion = (id: string) => {
    setOpenAccordion(openAccordion === id ? undefined : id);
  };

  const handlePermissionChange = (
    app: string,
    module: string,
    action: string,
    checked: boolean
  ) => {
    setSelected((prev: any) => {
      const updated = structuredClone(prev || {});
      if (!updated[app]) updated[app] = {};
      if (!updated[app][module]) updated[app][module] = {};

      updated[app][module][action] = checked;

      // After updating the action, check if all actions in the module are checked
      const actions =
        modulesList.find((m: any) => m.app_name === app)?.module_name_perm[
        module
        ] || [];
      const allActionsChecked = actions && actions.length > 0 && actions.every(
        (act: string) => updated[app][module][act] === true
      );

      // If all actions checked, ensure module is fully checked (implicitly handled by isModuleFullyChecked)
      // If not all actions checked, module is not fully checked

      // Check if all modulesList in the app are fully checked
      const allModulesChecked = Object.keys(updated[app]).every((modName) => {
        const modActions =
          modulesList.find((m: any) => m.app_name === app)?.module_name_perm[
          modName
          ] || [];
        return modActions.every(
          (act: string) => updated[app][modName][act] === true
        );
      });

      // No explicit state for module or app level checkboxes in selected, so no need to update separately
      // The parent checkboxes derive their checked state from isModuleFullyChecked and isAppFullyChecked

      return updated;
    });
  };

  const handleAppAllChange = (app: string, checked: boolean) => {
    const appData = modulesList.find((mod: any) => mod.app_name === app);

    setSelected((prev: any) => {
      const updated = structuredClone(prev || {});
      updated[app] = {};

      Object.entries(appData.module_name_perm).forEach(
        ([modName, actions]: [string, any]) => {
          updated[app][modName] = {};
          actions && actions.length > 0 && actions.forEach((action: string) => {
            updated[app][modName][action] = checked;
          });
        }
      );

      return updated;
    });
  };

  const handleModuleAllChange = (
    app: string,
    module: string,
    checked: boolean
  ) => {
    setSelected((prev: any) => {
      // const updated = { ...prev };
      const updated = structuredClone(prev || {});

      updated[app] = updated[app] || {};
      updated[app][module] = {};
      const actions =
        modulesList.find((m: any) => m.app_name === app)?.module_name_perm[
        module
        ] || [];
      actions && actions.length > 0 && actions.forEach((action: string) => {
        updated[app][module][action] = checked;
      });
      return updated;
    });
  };

  const handleGlobalAllChange = (checked: boolean) => {
    if (!modulesList || modulesList.length === 0) {
      console.warn("Modules not loaded yet");
      return;
    }

    const updated: any = {};
    modulesList.forEach((app: any) => {
      if (!app.app_name || !app.module_name_perm) return;

      updated[app.app_name] = {};
      Object.entries(app.module_name_perm).forEach(
        ([modName, actions]: [string, any]) => {
          updated[app.app_name][modName] = {};
          if (actions && Array.isArray(actions) && actions.length > 0) {
            actions.forEach((action: string) => {
              updated[app.app_name][modName][action] = checked;
            });
          }
        }
      );
    });

    setSelected(updated);
  };

  const isModuleFullyChecked = (app: string, module: string) => {
    const actions =
      modulesList.find((m: any) => m.app_name === app)?.module_name_perm[module] || [];
    if (!selected?.[app] || !selected[app][module]) {
      return false;
    }
    return actions?.length > 0 && actions.every(
      (action: string) => selected[app][module]?.[action] === true
    );
  };

  const isAppFullyChecked = (app: string) => {
    const appData = modulesList.find((m: any) => m.app_name === app);
    if (!selected?.[app]) {
      return false;
    }
    return Object.keys(appData?.module_name_perm || {}).every((modName) =>
      isModuleFullyChecked(app, modName)
    );
  };

  // This is the corrected function.
  const isGlobalFullyChecked = () => {
    // Check if the modulesList and selected state are available and not empty.
    if (!modulesList || modulesList.length === 0 || Object.keys(selected).length === 0) {
      return false;
    }

    // Iterate through each app in the modulesList array.
    return modulesList.every((app: any) => {
      const appName = app.app_name;

      // If the app is not in the selected state, it's not fully checked.
      if (!selected[appName]) {
        return false;
      }

      // Check if every module within the app is fully checked.
      return Object.entries(app.module_name_perm).every(([modName, actions]: [string, any]) => {
        // If the module is not in the selected state, it's not fully checked.
        if (!selected[appName][modName]) {
          return false;
        }

        // Check if every action within the module is checked.
        return actions?.length > 0 && actions.every((action: string) => selected[appName][modName][action]);
      });
    });
  };

  const updatePermision = async (payload: any) => {
    try {
      const update = await Axios.put(
        API_ENDPOINT.USER_MANAGEMENT.PERMISSION.UPDATE_PERMISSIONS_LIST(user),
        payload
      );
      return update.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const createPermision = async (payload: any) => {
    try {
      const create = await Axios.post(
        API_ENDPOINT.USER_MANAGEMENT.PERMISSION.CREATE_PERMISSIONS_LIST,
        payload
      );
      return create.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const apps_permissions = Object.entries(selected).map(
        ([apps_name, modulesObj]: any) => ({
          apps_name,
          modules_permissions: Object.entries(modulesObj).map(
            ([modules_name, permissions]: any) => ({
              modules_name,
              permissions,
            })
          ),
        })
      );

      const payload = {
        user_id: user || "",
        created_by: authUser?.username || "Test",
        updated_by: authUser?.username || "Test",
        apps_permissions,
      };

      let response = null;
      const result = await dispatch(fetchLatestPermissions()).unwrap();
      console.log(result, "result, line 350")
      // const latestPerms = result;
      // console.log(latestPerms,"latestPerms, line 351")
      // const categoryPerms = latestPerms
      // ?.find((p: any) => p.apps_name === "Masters")
      // ?.modules_permissions?.find(
      //   (m: any) => m.modules_name === "Category"
      // )?.permissions || {};
      if (result.length > 0) {
        response = await updatePermision(payload);
      }
      else {
        response = await createPermision(payload);
      }

      if (response !== null) {
        toast.success(response?.message);
        navigate("/user-list");
      } else {
        toast.error(
          `Unable to ${hasExistingPermissions ? "update" : "create"} permission`
        );
      }
    } catch (error: any) {
      console.error("Permission submission error:", error);
      toast.error(
        `Failed to ${hasExistingPermissions ? "update" : "create"} permissions`
      );
    } finally {
      setIsSubmitting(false);
    }
  };
console.log("modulesList in PermissionTab=>", JSON.stringify(modulesList));
  const LoadingSpinner = () => {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "50vh" }}>
        <div
          className="spinner-border text-primary"
          style={{ color: MecordColors.ThemeBGColor }}
          role="status"
        >
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  };

  if (isPageLoading) {
    return (
      <div className="mt-4 px-4 bg-white py-2 rounded-4 shadow-sm">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="mt-4 px-4 bg-white py-2 rounded-4 shadow-sm">
      <style>
        {`
          .form-check-input:checked {
            background-color: ${MecordColors.ThemeBGColor} !important;
            border-color: ${MecordColors.ThemeBGColor} !important;
          }
           .accordion-button:not(.collapsed) {
            background-color: ${MecordColors.ActiveBackground} !important;
            color: ${MecordColors.ActiveText} !important;
            box-shadow: ${MecordColors.Hover.boxShadow} !important;
          }
           .accordion-button:focus {
            box-shadow: none !important;
            border-color: rgba(0,0,0,.125);
           }
        `}
      </style>
      <h5 className="mb-3 mt-3">Permissions</h5>
      {/* <ToastContainer /> */}

      <Form.Group className="mb-3" controlId="globalAll">
        <Form.Check
          type="checkbox"
          label="All Permissions"
          checked={isGlobalFullyChecked()}
          onChange={(e) => handleGlobalAllChange(e.target.checked)}
          disabled={!modulesList || modulesList.length === 0}
        />
      </Form.Group>

      {modulesList.map((app: any, idx: number) => (
        <div key={idx} className="mb-3">
          <Accordion key={idx} activeKey={openAccordion}>
            <Accordion.Item eventKey={app.app_name}>
              <Accordion.Header onClick={() => toggleAccordion(app.app_name)}>
                { app.app_name}
              </Accordion.Header>
              <Accordion.Body>
                <Form.Group className="mb-2">
                  <Form.Check
                    type="checkbox"
                    label="All Permissions"
                    checked={isAppFullyChecked(app.app_name)}
                    onChange={(e) =>
                      handleAppAllChange(app.app_name, e.target.checked)
                    }
                  />
                </Form.Group>
                {Object.entries(app.module_name_perm).filter(([modName, actions]) => !modName.toLowerCase().includes("module")).map(
                  ([modName, actions]: [string, any], i: number) => (
                    <div key={i} className="ms-3 mt-3">
                      <strong>{modName == "Product" ?  "Sales" : modName}</strong>
                      <div className="ms-3 mb-2">
                        <Form.Check
                          type="checkbox"
                          label="All Actions"
                          checked={isModuleFullyChecked(app.app_name, modName)}
                          onChange={(e) =>
                            handleModuleAllChange(
                              app.app_name,
                              modName,
                              e.target.checked
                            )
                          }
                        />
                      </div>
                      <div className="ms-3 d-flex flex-wrap gap-3 mt-2">
                        {actions && actions.length > 0 && actions.map((action: string, j: number) => (
                          <Form.Check
                            key={j}
                            type="checkbox"
                            inline
                            label={action}
                            checked={
                              selected?.[app.app_name]?.[modName]?.[action] ||
                              false
                            }
                            onChange={(e) =>
                              handlePermissionChange(
                                app.app_name,
                                modName,
                                action,
                                e.target.checked
                              )
                            }
                          />
                        ))}
                      </div>
                    </div>
                  )
                )}
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </div>
      ))}

      <div className="d-flex flex-column flex-md-row justify-content-center gap-3 py-3">
        <CancelButton onClick={() => navigate("/user-list")} />
        <PrimarySubmitButton
          label={hasExistingPermissions ? "Update" : "Create"}
          onClick={handleSubmit}
          type="button"
          isLoading={isSubmitting}
          loadingLabel={hasExistingPermissions ? "Updating..." : "Creating..."}
        />
      </div>
    </div>
  );
};


export default PermissionTab;
